import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { PLATFORMS } from '../../constants/platforms';
import { PLATFORM_TOS } from '../../constants/platformCompliance';

const PLATFORM_META = PLATFORMS.reduce((acc, p) => { acc[p.value] = p; return acc; }, {});

const DATA_USAGE_DISCLOSURE = {
  YOUTUBE: 'OmniConnX will access your YouTube channel data including videos, analytics, and subscriber information. Data is used to display your channel metrics and publish content on your behalf. You can revoke access at any time from your Google account settings.',
  TIKTOK: 'OmniConnX will access your TikTok profile, videos, and engagement metrics. Data is used to display analytics and publish content on your behalf. You can revoke access at any time from your TikTok settings.',
  INSTAGRAM: 'OmniConnX will access your Instagram profile, media, and insights. Data is used to display your account metrics and publish content on your behalf. Data is handled in accordance with Meta Platform Terms.',
  FACEBOOK: 'OmniConnX will access your Facebook Pages, posts, and page insights. Data is used to display your page metrics and publish content on your behalf. You can revoke access at any time from your Facebook settings.',
  TWITTER: 'OmniConnX will access your Twitter/X profile, tweets, and engagement metrics. Data is used to display analytics and post tweets on your behalf. You can revoke access at any time from your Twitter settings.',
  TWITCH: 'OmniConnX will access your Twitch channel data including streams, followers, and subscriber information. Data is used to display your streaming metrics.',
};

const PreOAuthConsentDialog = ({ platform, onAccept, onCancel }) => {
  const [agreed, setAgreed] = useState(false);
  const meta = PLATFORM_META[platform] || {};
  const Icon = meta.icon;
  const tosUrl = PLATFORM_TOS[platform];
  const disclosure = DATA_USAGE_DISCLOSURE[platform] || 'OmniConnX will access your account data to display metrics and manage content on your behalf.';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onCancel}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          onClick={e => e.stopPropagation()}
          className="bg-white dark:bg-dark-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-700">
            <div className="flex items-center gap-3">
              <ShieldCheckIcon className="w-6 h-6 text-primary-600" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Connect {meta.label || platform}</h2>
            </div>
            <button
              onClick={onCancel}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 text-gray-400"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            {/* Platform icon + name */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
              {Icon && <Icon className={`w-8 h-8 ${meta.color || 'text-gray-600'}`} />}
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{meta.label || platform}</p>
                <p className="text-xs text-gray-500">Platform Connection</p>
              </div>
            </div>

            {/* Data usage disclosure */}
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Data Usage</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{disclosure}</p>
            </div>

            {/* Consent checkbox */}
            <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-dark-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                I agree to{' '}
                {tosUrl ? (
                  <a
                    href={tosUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                    onClick={e => e.stopPropagation()}
                  >
                    {meta.label || platform} Terms of Service
                  </a>
                ) : (
                  <span>{meta.label || platform} Terms of Service</span>
                )}{' '}
                and understand how my data will be used.
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-dark-700">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onAccept}
              disabled={!agreed}
              className="px-5 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to {meta.label || platform}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PreOAuthConsentDialog;
